import { LoadingSpinner } from "@faire/slate/components/loading-spinner";
import { CheckOutlineSolid } from "@faire/slate-icons/CheckOutlineSolid";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";

import {
  ANTICIPATION_BUILD_PHASES,
  MINIMAL_BRAND_MESSAGE,
  PROGRESS_CHECKLIST_STEPS,
  SPINNER_ROTATING_SUBTITLES,
} from "./data";
import type { TransitionVariantKey } from "./data";
import { EASE_OUT_EXPO, SignupStepHeader } from "./shared";

export interface TransitionStageProps {
  variant: TransitionVariantKey;
}

export const TransitionStage: React.FC<TransitionStageProps> = ({
  variant,
}) => {
  switch (variant) {
    case "progress-checklist":
      return <ProgressChecklistTransition />;
    case "anticipation-build":
      return <AnticipationBuildTransition />;
    case "minimal-brand":
      return <MinimalBrandMomentTransition />;
    case "spinner-subtitles":
    default:
      return <SpinnerRotatingSubtitleTransition />;
  }
};

const SUBTITLE_ROTATION_MS = 1400;

/** Mirrors the Brand Portal web-bridge "finishing up" modal: static title, rotating subtitle. */
const SpinnerRotatingSubtitleTransition: React.FC = () => {
  const reduceMotion = useReducedMotion() ?? false;
  const [subtitleIndex, setSubtitleIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setSubtitleIndex(
        (current) => (current + 1) % SPINNER_ROTATING_SUBTITLES.length
      );
    }, SUBTITLE_ROTATION_MS);
    return () => clearInterval(interval);
  }, []);

  return (
    <TransitionShell>
      <LoadingSpinner size="xLarge" />
      <h1 className="type-fs-display-s text-fs-text-primary text-balance">
        Setting up your payment terms
      </h1>
      <div className="flex h-[48px] items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.p
            key={subtitleIndex}
            initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 1 } : { opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: EASE_OUT_EXPO }}
            className="type-fs-paragraph text-fs-text-subdued text-balance"
          >
            {SPINNER_ROTATING_SUBTITLES[subtitleIndex]}
          </motion.p>
        </AnimatePresence>
      </div>
    </TransitionShell>
  );
};

const CHECKLIST_STEP_MS = 1300;

/** Concrete steps completing one at a time, so the wait explains what it's doing. */
const ProgressChecklistTransition: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (activeStep >= PROGRESS_CHECKLIST_STEPS.length) {
      return;
    }
    const timer = setTimeout(() => {
      setActiveStep((current) => current + 1);
    }, CHECKLIST_STEP_MS);
    return () => clearTimeout(timer);
  }, [activeStep]);

  return (
    <TransitionShell>
      <h1 className="type-fs-display-s text-fs-text-primary text-balance">
        Setting up your account
      </h1>
      <div className="flex w-full max-w-[340px] flex-col gap-(--slate-dimensions-4)">
        {PROGRESS_CHECKLIST_STEPS.map((label, index) => (
          <ChecklistRow
            key={label}
            label={label}
            state={
              index < activeStep
                ? "done"
                : index === activeStep
                  ? "active"
                  : "pending"
            }
          />
        ))}
      </div>
    </TransitionShell>
  );
};

type ChecklistRowState = "pending" | "active" | "done";

const ChecklistRow: React.FC<{ label: string; state: ChecklistRowState }> = ({
  label,
  state,
}) => {
  return (
    <div className="flex items-center gap-(--slate-dimensions-3)">
      <div className="flex size-6 shrink-0 items-center justify-center">
        {state === "done" ? (
          <CheckOutlineSolid className="text-fs-message-icon-success h-(--slate-dimensions-6) w-(--slate-dimensions-6)" />
        ) : state === "active" ? (
          <LoadingSpinner size="small" />
        ) : (
          <div className="border-fs-stroke-divider size-3 rounded-full border-2" />
        )}
      </div>
      <span
        className={
          state === "pending"
            ? "type-fs-paragraph text-fs-text-subdued"
            : "type-fs-paragraph text-fs-text-primary"
        }
      >
        {label}
      </span>
    </div>
  );
};

const ANTICIPATION_PHASE_MS = 1200;

/** The headline itself progresses, so the wait reads as a countdown to a reward. */
const AnticipationBuildTransition: React.FC = () => {
  const reduceMotion = useReducedMotion() ?? false;
  const [phaseIndex, setPhaseIndex] = useState(0);

  useEffect(() => {
    if (phaseIndex >= ANTICIPATION_BUILD_PHASES.length - 1) {
      return;
    }
    const timer = setTimeout(() => {
      setPhaseIndex((current) => current + 1);
    }, ANTICIPATION_PHASE_MS);
    return () => clearTimeout(timer);
  }, [phaseIndex]);

  return (
    <TransitionShell>
      <motion.div
        animate={
          reduceMotion ? { scale: 1 } : { scale: [1, 1.08, 1], opacity: 1 }
        }
        transition={{
          duration: 1.6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="bg-fs-surface-secondary flex size-16 items-center justify-center rounded-full"
      >
        <LoadingSpinner size="large" />
      </motion.div>
      <div className="flex h-[72px] items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.h1
            key={phaseIndex}
            initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 1 } : { opacity: 0, y: -14 }}
            transition={{ duration: 0.4, ease: EASE_OUT_EXPO }}
            className="type-fs-display-s text-fs-text-primary text-balance"
          >
            {ANTICIPATION_BUILD_PHASES[phaseIndex]}
          </motion.h1>
        </AnimatePresence>
      </div>
    </TransitionShell>
  );
};

/** Restrained version — one line, no rotating trivia — for a moment tied to money. */
const MinimalBrandMomentTransition: React.FC = () => {
  const reduceMotion = useReducedMotion() ?? false;

  return (
    <TransitionShell>
      <motion.span
        animate={
          reduceMotion ? { opacity: 1 } : { opacity: [0.4, 1, 0.4] }
        }
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        className="type-fs-display-m text-fs-text-primary tracking-[0.2em]"
      >
        FAIRE
      </motion.span>
      <p className="type-fs-paragraph text-fs-text-subdued">
        {MINIMAL_BRAND_MESSAGE}
      </p>
    </TransitionShell>
  );
};

const TransitionShell: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <div className="bg-fs-surface-primary flex min-h-full w-full flex-col">
      <SignupStepHeader progressPercent={92} />
      <div className="mx-auto flex w-full max-w-[480px] flex-1 flex-col items-center justify-center gap-(--slate-dimensions-6) px-(--slate-dimensions-6) py-(--slate-dimensions-10) text-center">
        {children}
      </div>
    </div>
  );
};
